import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface Campaign {
  id: number;
  name: string;
  subject: string;
  content_html: string;
  content_text?: string;
  segment_id?: number;
  segment_name?: string;
  segment_leads?: number;
  approval_status: 'draft' | 'pending' | 'approved' | 'rejected';
  publish_status: 'ready' | 'published' | 'scheduled' | 'sent';
  estimated_recipients: number;
  target_segment_count?: number;
  created_at: string;
  approved_at?: string;
  scheduled_for?: string;
  sent_at?: string;
  approval_token?: string;
  approval_feedback?: string;
  approved_by_email?: string;
  approval_version_number?: number;
  approval_content?: string;
  final_version_number?: number;
  ai_prompt?: string;
  voice_settings?: any;
  personalization_data?: any;
  generation_metadata?: any;
  mailerlite_stats?: any;
}

export interface GenerateContentRequest {
  segment_ids: number[];
  prompt: string;
  theme?: string;
  voice_settings?: any;
  include_resources?: boolean;
  use_knowledge_base?: boolean;
}

export interface GenerateContentResponse {
  success: boolean;
  campaigns_created: number;
  campaign_ids: number[];
  campaign_mode?: 'single' | 'individual';
  results: Array<{
    campaign_id: number;
    segment_name: string;
    subject: string;
    preview: string;
  }>;
}

export interface CampaignStats {
  sent: number;
  opens: number;
  clicks: number;
  bounced: number;
  unsubscribed: number;
  open_rate: number;
  click_rate: number;
}

class CampaignService {
  private getAuthHeaders() {
    return authService.getAuthHeaders();
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async getCampaigns(filters?: {
    status?: string;
    approval_status?: string;
  }): Promise<Campaign[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.approval_status) params.append('approval_status', filters.approval_status);

    const response = await fetch(`${API_BASE_URL}/api/campaigns?${params}`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async getCampaign(id: number): Promise<Campaign> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${id}`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async createCampaign(campaignData: {
    name: string;
    subject: string;
    content_html: string;
    content_text?: string;
    segment_id?: number;
  }): Promise<{ id: number; success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(campaignData)
    });

    return this.handleResponse(response);
  }

  async updateCampaign(id: number, updates: Partial<Campaign>): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${id}`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(updates)
    });

    return this.handleResponse(response);
  }

  async deleteCampaign(id: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${id}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async generateContent(request: GenerateContentRequest): Promise<GenerateContentResponse> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/generate-content`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(request)
    });

    return this.handleResponse(response);
  }

  async createApprovalLink(campaignId: number): Promise<{
    success: boolean;
    preview_url: string;
    token: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/create-approval-link`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async getPreviewByToken(token: string): Promise<{
    id: number;
    name: string;
    subject: string;
    content_html: string;
    content_text?: string;
    segment_name?: string;
    estimated_recipients: number;
    approval_status: string;
    created_at: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/preview/${token}`);
    return this.handleResponse(response);
  }

  async processApproval(token: string, approved: boolean, feedback?: string, email?: string): Promise<{
    success: boolean;
    status: string;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/preview/${token}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved, feedback, email })
    });

    return this.handleResponse(response);
  }

  async publishToMailerLite(campaignId: number): Promise<{
    success: boolean;
    mailerlite_campaign_id: number;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/publish`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async scheduleCampaign(campaignId: number, scheduledFor: string): Promise<{
    success: boolean;
    scheduled_for: string;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/schedule`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ scheduled_for: scheduledFor })
    });

    return this.handleResponse(response);
  }

  async cancelScheduledCampaign(campaignId: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/schedule`, {
      method: 'DELETE',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async sendCampaign(campaignId: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/send`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async getCampaignStats(campaignId: number): Promise<{
    status: string;
    stats: CampaignStats | null;
    campaign_info?: {
      name: string;
      subject: string;
      sent_at: string;
    };
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/stats`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  async getPersonalizationVariables(): Promise<{
    variables: string[];
    descriptions: Record<string, string>;
  }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/personalization-variables`, {
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  // Legacy approve method
  async approveCampaign(campaignId: number): Promise<{ success: boolean }> {
    const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/approve`, {
      method: 'POST',
      headers: this.getAuthHeaders()
    });

    return this.handleResponse(response);
  }

  // Utility methods
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getStatusColor(approval_status: string, publish_status: string): string {
    if (publish_status === 'sent') return 'green';
    if (publish_status === 'scheduled') return 'purple';
    if (publish_status === 'published') return 'blue';
    if (approval_status === 'approved') return 'green';
    if (approval_status === 'pending') return 'yellow';
    if (approval_status === 'rejected') return 'red';
    return 'gray';
  }

  getStatusText(approval_status: string, publish_status: string): string {
    if (publish_status === 'sent') return 'Sent';
    if (publish_status === 'scheduled') return 'Scheduled';
    if (publish_status === 'published') return 'Published';
    if (approval_status === 'approved') return 'Approved';
    if (approval_status === 'pending') return 'Pending Approval';
    if (approval_status === 'rejected') return 'Rejected';
    return 'Draft';
  }

  canEdit(campaign: Campaign): boolean {
    return campaign.approval_status === 'draft';
  }

  canSendForApproval(campaign: Campaign): boolean {
    return campaign.approval_status === 'draft';
  }

  canPublish(campaign: Campaign): boolean {
    return campaign.approval_status === 'approved' && campaign.publish_status === 'ready';
  }

  canSend(campaign: Campaign): boolean {
    return campaign.publish_status === 'published';
  }

  canSchedule(campaign: Campaign): boolean {
    return campaign.publish_status === 'published';
  }

  isReadyForMailerLite(campaign: Campaign): boolean {
    return campaign.approval_status === 'approved' && 
           ['ready', 'published'].includes(campaign.publish_status);
  }

  // Copy approval link to clipboard
  async copyApprovalLink(campaignId: number): Promise<string> {
    const result = await this.createApprovalLink(campaignId);
    await navigator.clipboard.writeText(result.preview_url);
    return result.preview_url;
  }

  // Generate preview content with personalization
  generatePreviewContent(content: string, sampleData: any = {}): string {
    const defaultData = {
      parent_name: 'Sarah Johnson',
      child_name: 'Emma',
      child_age: '8',
      timeline: 'Next semester',
      special_needs: 'Autism spectrum support',
      school_name: 'Bright Futures Academy',
      segment_name: 'Autism Support'
    };

    const data = { ...defaultData, ...sampleData };

    return content.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return data[key] || match;
    });
  }
}

const campaignService = new CampaignService();
export default campaignService;